import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    CaretUp, CaretDown, Equals, Trophy, ChatCircle, CheckCircle,
    TrendUp, TrendDown, Pause, Prohibit, Hourglass,
} from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { getUserProfile } from "../lib/api";

export default function Profile() {
    const { userId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState("predictions");

    useEffect(() => {
        getUserProfile(userId)
            .then(setData)
            .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"));
    }, [userId]);

    if (error === "not_found") {
        return (
            <AppShell>
                <div className="max-w-4xl mx-auto py-16 px-6 text-center">
                    <h1 className="font-display font-black text-3xl">User not found</h1>
                    <Link to="/" className="font-mono text-xs text-fa-yellow mt-4 inline-block">← Back</Link>
                </div>
            </AppShell>
        );
    }
    if (!data) {
        return (
            <AppShell>
                <div className="max-w-4xl mx-auto py-16 px-6 text-center text-white/50 font-mono text-sm">
                    Loading profile…
                </div>
            </AppShell>
        );
    }

    const { user, vote_stats, advice_stats, recent_votes, recent_advice, submitted_challenges, comment_count } = data;
    const activeStats = tab === "predictions" ? vote_stats : advice_stats;
    const activeRows = tab === "predictions" ? recent_votes : recent_advice;
    const accuracyPct = activeStats?.accuracy != null ? `${(activeStats.accuracy * 100).toFixed(0)}%` : "—";

    return (
        <AppShell>
            <div className="max-w-4xl mx-auto px-6 py-8 space-y-6" data-testid={`profile-${userId}`}>
                {/* Header */}
                <div className="border border-white/10 bg-[#0d0d0d] p-5 flex items-center gap-4">
                    <div className="w-16 h-16 bg-fa-yellow/10 border border-fa-yellow/40 flex items-center justify-center">
                        {user.picture ? (
                            <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                            <span className="font-display font-black text-2xl text-fa-yellow">
                                {(user.name || "?")[0]?.toUpperCase()}
                            </span>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h1 className="font-display font-black text-2xl uppercase tracking-tight truncate" data-testid="profile-name">
                            {user.name || "Anonymous manager"}
                        </h1>
                        <div className="font-mono text-[11px] text-white/50 mt-1 flex items-center gap-3 flex-wrap">
                            <span data-testid="profile-rep">
                                <Trophy size={11} weight="fill" className="inline mr-1 text-fa-yellow" />
                                {user.reputation} rep
                            </span>
                            <span>· {(vote_stats?.total ?? 0) + (advice_stats?.total ?? 0)} total casts</span>
                            <span>· {comment_count} comments</span>
                            {activeStats?.resolved > 0 && (
                                <span data-testid="profile-accuracy">
                                    · <CheckCircle size={11} weight="fill" className="inline mr-0.5 text-fa-green" />
                                    {accuracyPct} accuracy ({activeStats.correct}/{activeStats.resolved})
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tab switch */}
                <div className="flex border border-white/10 w-fit" data-testid="profile-tabs">
                    <TabButton
                        active={tab === "predictions"}
                        onClick={() => setTab("predictions")}
                        count={vote_stats?.total ?? 0}
                        testid="tab-predictions"
                    >
                        Predictions
                    </TabButton>
                    <TabButton
                        active={tab === "advice"}
                        onClick={() => setTab("advice")}
                        count={advice_stats?.total ?? 0}
                        testid="tab-advice"
                    >
                        Market Advice
                    </TabButton>
                </div>

                {/* Vote breakdown */}
                <section>
                    <h2 className="font-display font-black text-lg uppercase tracking-tight mb-3">
                        {tab === "predictions" ? "Voting Breakdown" : "Advice Breakdown"}
                    </h2>
                    {tab === "predictions" ? (
                        <div className="grid grid-cols-3 gap-3">
                            <StatCard label="Higher" value={vote_stats.higher} color="#9EE8B4" icon={<CaretUp size={16} weight="bold" />} testid="stat-higher" />
                            <StatCard label="Equal" value={vote_stats.equal} color="#9DB7F5" icon={<Equals size={16} weight="bold" />} testid="stat-equal" />
                            <StatCard label="Lower" value={vote_stats.lower} color="#FF6B6B" icon={<CaretDown size={16} weight="bold" />} testid="stat-lower" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            <StatCard label="Buy" value={advice_stats.buy} color="#9EE8B4" icon={<TrendUp size={16} weight="bold" />} testid="stat-buy" />
                            <StatCard label="Keep" value={advice_stats.keep} color="#9DB7F5" icon={<Pause size={16} weight="bold" />} testid="stat-keep" />
                            <StatCard label="Sell" value={advice_stats.sell} color="#FF6B6B" icon={<TrendDown size={16} weight="bold" />} testid="stat-sell" />
                            <StatCard label="Avoid" value={advice_stats.avoid} color="#F5A78D" icon={<Prohibit size={16} weight="bold" />} testid="stat-avoid" />
                            <StatCard label="Wait" value={advice_stats.wait} color="#E8C8A0" icon={<Hourglass size={16} weight="bold" />} testid="stat-wait" />
                        </div>
                    )}
                </section>

                {/* Recent activity */}
                <section>
                    <h2 className="font-display font-black text-lg uppercase tracking-tight mb-3">
                        {tab === "predictions" ? "Recent Votes" : "Recent Advice"}
                    </h2>
                    {!activeRows || activeRows.length === 0 ? (
                        <div className="font-mono text-[11px] text-white/40 italic border border-dashed border-white/10 p-6 text-center">
                            {tab === "predictions" ? "No votes yet." : "No advice cast yet."}
                        </div>
                    ) : tab === "predictions" ? (
                        <PredictionsTable rows={activeRows} />
                    ) : (
                        <AdviceTable rows={activeRows} />
                    )}
                </section>

                {/* Submitted challenges */}
                {submitted_challenges.length > 0 && (
                    <section>
                        <h2 className="font-display font-black text-lg uppercase tracking-tight mb-3 flex items-center gap-2">
                            <ChatCircle size={16} weight="fill" /> Submitted Challenges
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="submitted-challenges">
                            {submitted_challenges.map((c) => (
                                <div key={c.id} className="border border-white/10 bg-black/40 p-3">
                                    <div className="font-display font-bold">{c.title}</div>
                                    <div className="font-mono text-[10px] text-white/40 mt-1">
                                        Status: {c.status || "—"} · Quality: {c.qualityScore ?? "—"}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </AppShell>
    );
}

function TabButton({ active, onClick, count, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`px-4 py-2 font-mono text-[11px] uppercase tracking-widest border-r border-white/10 last:border-r-0 transition-colors ${
                active
                    ? "bg-fa-violet/30 text-fa-cream"
                    : "bg-transparent text-white/60 hover:text-white hover:bg-white/5"
            }`}
        >
            {children}
            <span className="ml-2 text-white/40">({count})</span>
        </button>
    );
}

function PredictionsTable({ rows }) {
    return (
        <div className="border border-white/10 overflow-hidden" data-testid="recent-votes-list">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-white/5 border-b border-white/10 text-left">
                        <Th>Player</Th>
                        <Th>GW Cast</Th>
                        <Th>Window</Th>
                        <Th>Conviction</Th>
                        <Th>Projected at cast</Th>
                        <Th>Outcome</Th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((v) => (
                        <tr key={`${v.player_id}-${v.gw_window}-${v.updated_at || ""}`} className="border-b border-white/5" data-testid={`vote-row-${v.player_id}-${v.gw_window}`}>
                            <Td className="font-mono text-xs"><PlayerCell pid={v.player_id} info={v.player} /></Td>
                            <Td className="font-mono text-xs text-fa-cream">
                                GW{v.target_gw_start ?? "—"}
                            </Td>
                            <Td className="font-mono text-xs text-white/60">{v.gw_window}w</Td>
                            <Td><ConvictionBadge conviction={v.conviction} /></Td>
                            <Td className="font-mono text-xs">
                                {v.predicted_at_cast != null ? `${v.predicted_at_cast.toFixed(1)} pts` : "—"}
                            </Td>
                            <Td>
                                {v.resolved ? (
                                    <span
                                        className="font-mono text-[10px] uppercase tracking-widest"
                                        style={{ color: v.was_correct ? "#9EE8B4" : "#FF6B6B" }}
                                    >
                                        {v.was_correct ? "+5 rep · correct" : "missed"}
                                        {v.actual_total != null && ` · ${v.actual_total.toFixed(1)} pts`}
                                    </span>
                                ) : (
                                    <span className="font-mono text-[10px] text-white/40 italic">pending</span>
                                )}
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function AdviceTable({ rows }) {
    return (
        <div className="border border-white/10 overflow-hidden" data-testid="recent-advice-list">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-white/5 border-b border-white/10 text-left">
                        <Th>Player</Th>
                        <Th>GW Cast</Th>
                        <Th>Advice</Th>
                        <Th>Snapshot · Cost</Th>
                        <Th>Outcome</Th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((a) => (
                        <tr
                            key={`${a.player_id}-${a.updated_at || a.created_at || ""}`}
                            className="border-b border-white/5"
                            data-testid={`advice-row-${a.player_id}`}
                        >
                            <Td className="font-mono text-xs"><PlayerCell pid={a.player_id} info={a.player} /></Td>
                            <Td className="font-mono text-xs text-fa-cream">
                                GW{a.gw_at_cast ?? "—"}
                            </Td>
                            <Td><AdviceBadge advice={a.advice} /></Td>
                            <Td className="font-mono text-xs text-white/70">
                                {a.snapshot?.total_points ?? "—"} pts
                                {a.snapshot?.now_cost != null && (
                                    <> · £{(a.snapshot.now_cost / 10).toFixed(1)}m</>
                                )}
                            </Td>
                            <Td>
                                {a.resolved ? (
                                    <span
                                        className="font-mono text-[10px] uppercase tracking-widest"
                                        style={{ color: a.correct === true ? "#9EE8B4" : a.correct === false ? "#FF6B6B" : "#888" }}
                                    >
                                        {a.correct === true && "+5 rep · correct"}
                                        {a.correct === false && "missed"}
                                        {a.correct == null && "neutral"}
                                        {a.resolution?.delta_points != null && (
                                            <> · Δ{a.resolution.delta_points}p</>
                                        )}
                                    </span>
                                ) : (
                                    <span className="font-mono text-[10px] text-white/40 italic">pending</span>
                                )}
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PlayerCell({ pid, info }) {
    const name = info?.name;
    const team = info?.team_short;
    const pos = info?.position;
    if (!name) {
        return <span className="text-white/50">#{pid}</span>;
    }
    return (
        <span data-testid={`player-cell-${pid}`}>
            <span className="text-white">{name}</span>
            {(team || pos) && (
                <span className="text-white/40 ml-1.5 text-[10px]">
                    {team}{team && pos ? " · " : ""}{pos}
                </span>
            )}
        </span>
    );
}

function StatCard({ label, value, color, icon, testid }) {
    return (
        <div className="border border-white/10 bg-black/40 p-3" data-testid={testid}>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest" style={{ color }}>
                {icon} {label}
            </div>
            <div className="font-display font-black text-3xl mt-1 tabular-nums">{value}</div>
        </div>
    );
}

function ConvictionBadge({ conviction }) {
    const cfg = {
        HIGHER: { color: "#9EE8B4", icon: CaretUp },
        EQUAL: { color: "#9DB7F5", icon: Equals },
        LOWER: { color: "#FF6B6B", icon: CaretDown },
    }[conviction] || { color: "#888", icon: Equals };
    const Icon = cfg.icon;
    return (
        <span className="font-mono text-[10px] uppercase tracking-widest flex items-center gap-1" style={{ color: cfg.color }}>
            <Icon size={12} weight="bold" /> {conviction}
        </span>
    );
}

function AdviceBadge({ advice }) {
    const cfg = {
        BUY:   { color: "#9EE8B4", icon: TrendUp },
        KEEP:  { color: "#9DB7F5", icon: Pause },
        SELL:  { color: "#FF6B6B", icon: TrendDown },
        AVOID: { color: "#F5A78D", icon: Prohibit },
        WAIT:  { color: "#E8C8A0", icon: Hourglass },
    }[advice] || { color: "#888", icon: Pause };
    const Icon = cfg.icon;
    return (
        <span className="font-mono text-[10px] uppercase tracking-widest flex items-center gap-1" style={{ color: cfg.color }}>
            <Icon size={12} weight="bold" /> {advice || "—"}
        </span>
    );
}

const Th = ({ children }) => (
    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/50">
        {children}
    </th>
);
const Td = ({ children, className = "" }) => (
    <td className={`px-3 py-2 ${className}`}>{children}</td>
);
